'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import {
  ArrowLeft, Bookmark, BookmarkCheck, Share2, ExternalLink,
  Clock, MapPin, Calendar, Radio, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';
import BottomNav from '@/components/BottomNav';
import NewsCard from '@/components/NewsCard';
import { MOCK_ARTICLES, getTimeAgo, getCategoryBySlug, type NewsArticle } from '@/lib/mockData';

export default function NewsDetailClient() {
  const searchParams = useSearchParams();
  const articleId = searchParams.get('id') || 'article-001';

  const [lang, setLang] = useState<'hi' | 'en'>('hi');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>([]);
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const storedLang = localStorage.getItem('nj-lang') as 'hi' | 'en' | null;
    if (storedLang) setLang(storedLang);

    const storedBookmarks = localStorage.getItem('nj-bookmarks');
    if (storedBookmarks) {
      try {
        const parsed = new Set<string>(JSON.parse(storedBookmarks));
        setBookmarks(parsed);
        setIsBookmarked(parsed.has(articleId));
      } catch {
        // ignore
      }
    }
  }, [articleId]);

    useEffect(() => {
    let cancelled = false;

    async function loadArticle() {
      setIsLoading(true);
      setArticle(null);
      setRelatedArticles([]);

      try {
        const response = await fetch('/api/news', {
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }

        const data = await response.json();
        const articles: NewsArticle[] = Array.isArray(data.articles)
          ? data.articles
          : [];

        if (cancelled) return;

        const found = articles.find(
          (item) => String(item.id) === String(articleId)
        );

        if (!found) {
          setArticle(null);
          setRelatedArticles([]);
          return;
        }

        setArticle(found);

        const related = articles
          .filter(
            (item) =>
              item.id !== found.id &&
              (item.category === found.category ||
                (!!item.district && item.district === found.district))
          )
          .slice(0, 4);

        setRelatedArticles(
          related.length > 0
            ? related
            : articles.filter((item) => item.id !== found.id).slice(0, 4)
        );
      } catch (error) {
        console.error('Error loading article:', error);

        if (!cancelled) {
          setArticle(null);
          setRelatedArticles([]);
          toast.error(
            lang === 'hi'
              ? 'खबर लोड नहीं हो सकी'
              : 'Could not load the article'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadArticle();

    return () => {
      cancelled = true;
    };
  }, [articleId, lang]);
  const handleBookmarkToggle = () => {
    if (!article) return;
    const next = !isBookmarked;
    setIsBookmarked(next);
    const newBookmarks = new Set(bookmarks);
    if (next) {
      newBookmarks.add(article.id);
      toast.success(lang === 'hi' ? 'बुकमार्क सेव हो गया' : 'Article bookmarked');
    } else {
      newBookmarks.delete(article.id);
      toast.info(lang === 'hi' ? 'बुकमार्क हटाया गया' : 'Bookmark removed');
    }
    setBookmarks(newBookmarks);
    localStorage.setItem('nj-bookmarks', JSON.stringify([...newBookmarks]));
  };

  const handleShare = async () => {
    if (!article) return;
    const title = lang === 'hi' ? article.titleHindi : article.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: window.location.href });
      } catch {
        // user cancelled share
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(lang === 'hi' ? 'लिंक कॉपी हो गया' : 'Link copied to clipboard');
    }
  };

  const handleRelatedBookmark = (id: string) => {
    const next = new Set(bookmarks);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setBookmarks(next);
    localStorage.setItem('nj-bookmarks', JSON.stringify([...next]));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* Skeleton header */}
        <div className="sticky top-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
          <div className="flex-1">
            <div className="h-4 w-48 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="skeleton-pulse h-64 sm:h-80 w-full" />
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 space-y-4">
          <div className="flex gap-2">
            <div className="h-5 w-20 rounded-full bg-muted animate-pulse" />
            <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />
          </div>
          <div className="h-8 w-full bg-muted rounded animate-pulse" />
          <div className="h-8 w-4/5 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
          <div className="space-y-2 pt-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`detail-skel-${i + 1}`} className="h-4 bg-muted rounded animate-pulse" style={{ width: `${85 + (i % 3) * 5}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4">
        <p className="font-hindi text-foreground text-lg">खबर नहीं मिली</p>
        <Link href="/" className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-hindi font-medium">
          होम पर जाएं
        </Link>
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);
  const title = lang === 'hi' ? article.titleHindi : article.title;
  const summary = lang === 'hi' ? article.summaryHindi : article.summary;
  const content = article.content || summary;
  const timeAgo = getTimeAgo(article.publishedAt, lang);

  const publishedDate = new Date(article.publishedAt);
  const formattedDate = `${publishedDate.getDate()} ${publishedDate.toLocaleString('hi-IN', { month: 'long' })} ${publishedDate.getFullYear()}`;
  const formattedTime = publishedDate.toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top navigation bar */}
      <div className="sticky top-0 z-50 bg-card border-b border-border">
        {/* Red strip */}
        <div className="bg-primary h-1" />
        <div className="px-4 py-2.5 flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3">
            <Link
              href="/"
              className="p-2 rounded-xl hover:bg-muted transition-colors btn-press flex items-center gap-1 text-sm font-hindi text-muted-foreground"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:block">{lang === 'hi' ? 'वापस' : 'Back'}</span>
            </Link>
            <div className="flex items-center gap-2">
              <AppLogo size={24} />
              <span className="font-hindi font-bold text-primary text-sm hidden sm:block">नमस्ते झारखंड</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="p-2 rounded-xl hover:bg-muted transition-colors btn-press"
              aria-label="Share article"
            >
              <Share2 size={16} className="text-muted-foreground" />
            </button>
            <button
              onClick={handleBookmarkToggle}
              className="p-2 rounded-xl hover:bg-muted transition-colors btn-press"
              aria-label="Bookmark article"
            >
              {isBookmarked ? (
                <BookmarkCheck size={16} className="text-yellow-500" />
              ) : (
                <Bookmark size={16} className="text-muted-foreground" />
              )}
            </button>
            <button
              onClick={() => {
                const next = lang === 'hi' ? 'en' : 'hi';
                setLang(next);
                localStorage.setItem('nj-lang', next);
              }}
              className="px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted text-xs font-semibold transition-colors btn-press"
            >
              {lang === 'hi' ? 'EN' : 'हि'}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-screen-2xl mx-auto w-full pb-24 lg:pb-8">
        <div className="lg:grid lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:px-8 xl:px-10 lg:pt-6">
          {/* Article content — 2/3 or 3/4 width */}
          <article className="lg:col-span-2 xl:col-span-3">
            {/* Hero image */}
            <div className="relative h-64 sm:h-80 lg:h-96 bg-muted overflow-hidden lg:rounded-2xl">
              <AppImage
                src={article.image}
                alt={article.imageAlt}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 75vw"
                className="object-cover"
                priority
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

              {/* Sample data notice */}
              {article.isSampleData && (
                <div className="absolute top-3 left-3">
                  <span className="sample-data-badge text-[10px]">
                    {lang === 'hi' ? '⚠️ सैंपल डेटा' : '⚠️ SAMPLE DATA'}
                  </span>
                </div>
              )}
            </div>

            {/* Article body */}
            <div className="px-4 lg:px-0 pt-5 pb-6">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.isBreaking && (
                  <span className="breaking-badge">
                    <Radio size={10} className="animate-pulse" />
                    {lang === 'hi' ? 'ब्रेकिंग न्यूज़' : 'BREAKING NEWS'}
                  </span>
                )}
                {category && (
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full font-hindi"
                    style={{ color: category.color, backgroundColor: category.bgColor }}
                  >
                    {lang === 'hi' ? category.labelHindi : category.labelEnglish}
                  </span>
                )}
                {article.districtHindi && (
                  <span className="text-muted-foreground text-xs flex items-center gap-1 font-hindi">
                    <MapPin size={11} />
                    {lang === 'hi' ? article.districtHindi : article.district}
                  </span>
                )}
              </div>

              {/* Headline */}
              <h1 className="font-hindi font-bold text-xl sm:text-2xl lg:text-3xl text-foreground leading-snug mb-4">
                {title}
              </h1>

              {/* Metadata row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5 pb-5 border-b border-border">
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary text-xs font-bold">{article.source[0]}</span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">{article.source}</p>
                    <p className="text-[10px] text-muted-foreground font-hindi">
                      {lang === 'hi' ? 'स्रोत' : 'Source'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 text-muted-foreground text-xs font-hindi">
                  <Calendar size={12} />
                  <span>{formattedDate}</span>
                </div>

                <div className="flex items-center gap-1 text-muted-foreground text-xs font-hindi">
                  <Clock size={12} />
                  <span>{formattedTime} ({timeAgo})</span>
                </div>
              </div>

             {/* Article summary and full content */}
<div className="prose prose-sm max-w-none">
  <p className="font-hindi text-foreground text-base leading-relaxed mb-4 whitespace-pre-wrap">
    {summary}
  </p>

  {content.trim() !== summary.trim() && (
    <div className="font-hindi text-foreground/90 text-sm leading-relaxed whitespace-pre-wrap">
      {content}
    </div>
  )}
</div>

              {/* Read original CTA */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={article.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-xl font-hindi font-bold text-sm hover:bg-primary-dark transition-colors btn-press"
                >
                  <ExternalLink size={16} />
                  {lang === 'hi' ? 'पूरी खबर पढ़ें' : 'Read Full Article'}
                  <span className="text-xs opacity-75">— {article.source}</span>
                </a>
                <button
                  onClick={handleShare}
                  className="flex items-center justify-center gap-2 px-5 py-3 border border-border rounded-xl font-hindi font-medium text-sm hover:bg-muted transition-colors btn-press"
                >
                  <Share2 size={16} />
                  {lang === 'hi' ? 'शेयर करें' : 'Share'}
                </button>
              </div>

              {/* Source attribution */}
              <p className="text-muted-foreground text-[11px] font-hindi mt-4 text-center">
                {lang === 'hi'
                  ? `यह खबर ${article.source} से ली गई है। कॉपीराइट © ${article.source}`
                  : `This article is sourced from ${article.source}. Copyright © ${article.source}`}
              </p>
            </div>
          </article>

          {/* Sidebar — related / trending */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-20 space-y-6">
              {/* Related articles */}
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  <h3 className="font-hindi font-bold text-sm text-foreground">
                    {lang === 'hi' ? 'संबंधित खबरें' : 'Related News'}
                  </h3>
                </div>
                <div className="divide-y divide-border">
                  {relatedArticles.map((rel) => (
                    <NewsCard
                      key={rel.id}
                      article={rel}
                      lang={lang}
                      variant="compact"
                      onBookmarkToggle={handleRelatedBookmark}
                      isBookmarked={bookmarks.has(rel.id)}
                    />
                  ))}
                </div>
                <div className="px-4 py-3 border-t border-border">
                  <Link
                    href="/"
                    className="flex items-center justify-center gap-1 text-primary text-xs font-hindi font-medium hover:underline"
                  >
                    {lang === 'hi' ? 'और खबरें देखें' : 'See more news'}
                    <ChevronRight size={12} />
                  </Link>
                </div>
              </div>

              {/* E-Paper widget */}
              <div className="bg-gradient-to-br from-primary to-red-800 rounded-2xl p-4 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest mb-1 opacity-80">
                  {lang === 'hi' ? 'डिजिटल संस्करण' : 'Digital Edition'}
                </p>
                <p className="font-hindi font-bold text-base mb-3">
                  {lang === 'hi' ? 'ई-पेपर पढ़ें' : 'Read E-Paper'}
                </p>
                <a
                  href="https://www.magzter.com/publishers/Edutech-Ventures-and-Services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-primary rounded-xl text-xs font-hindi font-bold btn-press hover:bg-gray-100 transition-colors"
                >
                  📰 {lang === 'hi' ? 'Magzter पर खोलें' : 'Open on Magzter'}
                </a>
              </div>
            </div>
          </aside>
        </div>

        {/* Mobile related articles */}
        <div className="lg:hidden px-4 pt-2 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-primary rounded-full" />
            <h3 className="font-hindi font-bold text-base text-foreground">
              {lang === 'hi' ? 'संबंधित खबरें' : 'Related News'}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {relatedArticles.map((rel) => (
              <NewsCard
                key={rel.id}
                article={rel}
                lang={lang}
                variant="default"
                onBookmarkToggle={handleRelatedBookmark}
                isBookmarked={bookmarks.has(rel.id)}
              />
            ))}
          </div>
        </div>
      </main>

      <BottomNav lang={lang} />
    </div>
  );
}